import {
  Article,
  DeleteOutline,
  EditOutlined,
  FileDownloadOutlined,
  PictureInPictureOutlined,
  UploadFileOutlined,
} from '@mui/icons-material'
import { Button } from '@mui/material'
import { MRT_ColumnDef } from 'material-react-table'
import React, { FunctionComponent, useMemo } from 'react'
import { Link } from 'react-router-dom'

import StackMenu from '../../../../base/components/MyMenu/StackMenu'
import { StackMenuItem } from '../../../../base/components/MyMenu/StackMenuItem'
import { FilterTypeMap } from '../../../../base/components/Table/castMrtFilters.ts'
import { genMrtQueryPagination } from '../../../../base/components/Table/genMrtQueryPagination.ts'
import { MrtDynamicTable } from '../../../../base/components/Table/MrtDynamicTable.tsx'
import { MrtTableConfig } from '../../../../base/components/Table/mrtTypes.ts'
import { useMrtQuery } from '../../../../base/components/Table/useMrtQuery.tsx'
import { ArticuloProps } from '../../../../interfaces/articulo.ts'
import { genPath } from '../../../../utils/genPath.ts'
import { apiArticuloListado } from '../../api/apiArticuloListado'
import { articuloRoutesMap } from '../../articuloRoutes'
import { useArticuloOperacion } from '../../hook/useArticuloOperacion.tsx'
import { articuloColumns } from './articuloColumns'
import ArticuloImportarActualizarDialog from './ArticuloImportarActualizarDialog.tsx'
import ArticuloImportarDialog from './ArticuloImportarDialog.tsx'
import ArticuloImportarImagenDialog from './ArticuloImportarImagenDialog.tsx'

interface OwnProps {}

type Props = OwnProps

const FILTER_TYPES: FilterTypeMap<ArticuloProps> = {
  articuloCompra: 'boolean',
  articuloVenta: 'boolean',
  articuloProduccion: 'boolean',
  activo: 'boolean',
  verificarStock: 'boolean',
  tipoArticulo: 'oid',
}

/**
 * Listado o datatable de articulos
 * @constructor
 */
const ArticuloListado: FunctionComponent<Props> = () => {
  const {
    onActionImportar,
    action,
    exportarDatos,
    onActionReset,
    onActionImportarImagen,
    onActionActualizarMasivo,
    onEliminar,
  } = useArticuloOperacion()
  const columns = useMemo<MRT_ColumnDef<ArticuloProps>[]>(() => articuloColumns, [])

  const listado = useMrtQuery({
    queryKey: ['articulo-listado'],
    queryFn: async (ctx) => {
      const pgs = genMrtQueryPagination(ctx, { filterTypes: FILTER_TYPES })
      return apiArticuloListado(pgs)
    },
    isServerSide: true,
    queryOptions: {
      refetchOnWindowFocus: false,
      refetchInterval: false,
    },
  })

  const config: MrtTableConfig<ArticuloProps> = {
    id: 'listado-articulos',
    columns,
    showIconRefetch: true,
    showAudit: true,
    rowIconsActions: [
      {
        label: 'Editar',
        icon: <EditOutlined />,
        to: (row) => genPath(articuloRoutesMap.actualizar.path, { id: row._id }),
      },
    ],
    rowMenuActions: [
      {
        label: 'Importar Imagen',
        icon: <PictureInPictureOutlined />,
        onClick: ({ row }) => onActionImportarImagen(row),
      },
      {
        label: 'Eliminar',
        icon: <DeleteOutline />,
        color: 'error',
        onClick: ({ row, refetch }) => onEliminar(row, () => refetch()),
      },
    ],
    renderTopToolbarCustomActions: () => {
      return (
        <Button
          size={'small'}
          color={'info'}
          variant={'text'}
          startIcon={<FileDownloadOutlined />}
          onClick={async () => await exportarDatos()}
        >
          Exportar articulos
        </Button>
      )
    },
  }

  return (
    <>
      <StackMenu asideSidebarFixed>
        <StackMenuItem>
          <Button
            variant="contained"
            onClick={onActionActualizarMasivo}
            startIcon={<UploadFileOutlined />}
            color="info"
          >
            Actualización masiva
          </Button>
        </StackMenuItem>

        <StackMenuItem>
          <Button
            variant="contained"
            onClick={onActionImportar}
            startIcon={<UploadFileOutlined />}
            color="secondary"
          >
            Importar articulos
          </Button>
        </StackMenuItem>

        <StackMenuItem>
          <Button
            variant="contained"
            component={Link}
            to={articuloRoutesMap.registro.path}
            startIcon={<Article />}
            color="primary"
          >
            {articuloRoutesMap.registro.name}
          </Button>
        </StackMenuItem>
      </StackMenu>

      <MrtDynamicTable config={config} {...listado} />

      {action.mode === 'importarImagen' && (
        <ArticuloImportarImagenDialog
          id={'importarImagen'}
          keepMounted={false}
          open={action.open}
          row={action.data}
          onClose={async () => {
            await listado.refetch()
            onActionReset()
          }}
        />
      )}

      {action.mode === 'importar' && (
        <ArticuloImportarDialog
          id={'importarArticulo'}
          keepMounted={false}
          open={action.open}
          onClose={async (value?: boolean) => {
            if (value) await listado.refetch()
            onActionReset()
          }}
        />
      )}

      {action.mode === 'actualizarMasivo' && (
        <ArticuloImportarActualizarDialog
          id={'actualizar-masiva-articulo'}
          keepMounted={false}
          open={action.open}
          onClose={async (value) => {
            if (value) await listado.refetch()
            onActionReset()
          }}
        />
      )}
    </>
  )
}

export default ArticuloListado
